// ── P2: client-side op engine for concurrent script editing ──────────────────
// The editor saves a *diff* of operations against its last-confirmed body instead
// of the whole script, so two people editing different cells/rows both land (no
// clobber). Rows are identified by their stable `id` (P0), never by index.
//
//   diffToOps(oldBody, newBody) → ops[]   — what changed, as setCell/insert/delete/move
//   applyOps(body, ops)         → newBody — replays ops locally (mirrors the server's
//                                           applyOps) for reconciling others' changes
//
// The server (`PATCH /api/scripts/:id/ops`) is authoritative: it applies the ops onto
// the *current* server body and returns the merged result. applyOps here is only used
// to fold the user's own still-un-acked edits back on top of that merged body.

// Minimal id minter — only used as a fallback when an insertRow op carries no id.
function uid() {
  try { return crypto.randomUUID(); }
  catch { return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
}

const isMetaKey = (k) => k.charCodeAt(0) === 95; // '_' prefix: _rowColor, _links, _cellColors…

function sameOrder(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// Longest common subsequence of two id arrays → the ids that did NOT move. Anything
// not in the LCS needs a moveRow to reach the target order.
function lcs(a, b) {
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = 1; i <= n; i++)
    for (let j = 1; j <= m; j++)
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
  const res = [];
  let i = n, j = m;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) { res.push(a[i - 1]); i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) i--; else j--;
  }
  return res.reverse();
}

// Produce the ops that transform oldBody → newBody. Order matters: deletes, then
// inserts (ascending so chained new rows anchor correctly), then setCells, then moves.
export function diffToOps(oldBody, newBody) {
  const oldScenes = (oldBody && Array.isArray(oldBody.scenes)) ? oldBody.scenes : [];
  const newScenes = (newBody && Array.isArray(newBody.scenes)) ? newBody.scenes : [];
  const oldById = new Map(oldScenes.filter(s => s && s.id).map(s => [s.id, s]));
  const newById = new Map(newScenes.filter(s => s && s.id).map(s => [s.id, s]));
  const ops = [];

  // Deletes — present in old, gone in new.
  for (const s of oldScenes) {
    if (s && s.id && !newById.has(s.id)) ops.push({ type: 'deleteRow', rowId: s.id });
  }

  // Inserts — present in new, not in old. Anchor after the immediate predecessor in the
  // new order (null = head). Ascending order means a run of new rows chains correctly.
  for (let i = 0; i < newScenes.length; i++) {
    const s = newScenes[i];
    if (!s || !s.id || oldById.has(s.id)) continue;
    const afterRowId = i > 0 && newScenes[i - 1] ? newScenes[i - 1].id : null;
    ops.push({ type: 'insertRow', afterRowId, row: s });
  }

  // setCell — for rows present in both, diff every column. Text cols carry `fromValue`
  // (compare-and-set → same-cell conflict detection); metadata (`_…`) cols are pure LWW.
  for (const s of newScenes) {
    if (!s || !s.id) continue;
    const old = oldById.get(s.id);
    if (!old) continue; // a fresh insert — its values already travel with insertRow
    const keys = new Set([...Object.keys(old), ...Object.keys(s)]);
    for (const col of keys) {
      if (col === 'id') continue;
      if (isMetaKey(col)) {
        if (JSON.stringify(old[col]) !== JSON.stringify(s[col])) {
          ops.push({ type: 'setCell', rowId: s.id, col, toValue: s[col] }); // no fromValue → LWW
        }
      } else {
        const av = old[col] == null ? '' : old[col];
        const bv = s[col] == null ? '' : s[col];
        if (av !== bv) ops.push({ type: 'setCell', rowId: s.id, col, fromValue: av, toValue: bv });
      }
    }
  }

  // moveRow — reconcile the order of rows surviving in both. Only the rows outside the
  // LCS move; each is anchored after its predecessor in the new common order.
  const commonOld = oldScenes.filter(s => s && s.id && newById.has(s.id)).map(s => s.id);
  const commonNew = newScenes.filter(s => s && s.id && oldById.has(s.id)).map(s => s.id);
  if (!sameOrder(commonOld, commonNew)) {
    const keep = new Set(lcs(commonOld, commonNew));
    for (let i = 0; i < commonNew.length; i++) {
      const id = commonNew[i];
      if (keep.has(id)) continue;
      ops.push({ type: 'moveRow', rowId: id, afterRowId: i > 0 ? commonNew[i - 1] : null });
    }
  }

  return ops;
}

// Replay ops onto a body. Mirrors the server's applyOps so local reconciliation matches
// what the server will compute. Never mutates input.
export function applyOps(body, ops) {
  const base = (body && Array.isArray(body.scenes))
    ? body
    : { format: (body && body.format) || 'None', scenes: [] };
  const scenes = base.scenes.map(s => ({ ...s }));
  const idx = (id) => scenes.findIndex(s => s.id === id);
  for (const op of (Array.isArray(ops) ? ops : [])) {
    if (!op || typeof op !== 'object') continue;
    if (op.type === 'setCell') {
      const i = idx(op.rowId);
      if (i === -1) continue;
      scenes[i] = { ...scenes[i], [op.col]: op.toValue ?? '' };
    } else if (op.type === 'insertRow') {
      const row = { ...(op.row || {}) };
      if (!row.id || scenes.some(s => s.id === row.id)) row.id = uid();
      let at = scenes.length;
      if (op.afterRowId === null) at = 0;
      else if (op.afterRowId !== undefined) { const j = idx(op.afterRowId); at = j === -1 ? scenes.length : j + 1; }
      scenes.splice(at, 0, row);
    } else if (op.type === 'deleteRow') {
      const i = idx(op.rowId);
      if (i !== -1) scenes.splice(i, 1);
    } else if (op.type === 'moveRow') {
      const i = idx(op.rowId);
      if (i === -1) continue;
      const [row] = scenes.splice(i, 1);
      let at = scenes.length;
      if (op.afterRowId === null) at = 0;
      else { const j = idx(op.afterRowId); at = j === -1 ? scenes.length : j + 1; }
      scenes.splice(at, 0, row);
    }
  }
  return { ...base, scenes };
}
